import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';

import { exportRegister } from './register-export.js';
import {
  ApiError,
  DISCIPLINES,
  createDeliverable,
  deleteDeliverable,
  issueRevision,
  listDeliverables,
  listSubmittals,
  moveDeliverable,
  updateDeliverable,
  type Deliverable,
  type DeliverableStatus,
  type Discipline,
  type Submittal,
} from '../api.js';
import { formatDate } from '../labels.js';
import { AppLink } from '../link.js';
import { navigate } from '../router.js';
import { Attachments } from './Attachments.js';
import { CrossLink, focusClass, useFocusRow } from './cross-links.js';
import { recordHref, sectionHref } from './project-sections.js';
import { RecordBack, RecordMissing, RecordPage } from './record-page.js';
import {
  Drawer,
  RegisterToolbar,
  RowActions,
  matches,
  useRegisterView,
  type RowAction,
} from './register-kit.js';
import { LOAD_ALL_LIMIT } from '../api/paging.js';

/**
 * The drawings register (DS-01 / DS-02).
 *
 * The screen's job is to answer two questions a Design Coordinator is asked
 * every week: what is still owed, and what is late. So the late rows say so in
 * words on the row itself, and the register leads with a count rather than
 * making somebody scan for red.
 *
 * **There is no "mark as issued" button, on purpose.** A drawing becomes issued
 * by issuing a revision, which is what the button says. The alternative is a
 * status field that satisfies the Design stage gate without anybody having had
 * to produce a drawing.
 *
 * ON THE REGISTER PATTERN, the second caller after `Materials.tsx`
 * ---------------------------------------------------------------
 * `register-kit.tsx` does the toolbar, the menu's keyboard model, the drawer and
 * its unsaved-change protection. What is decided HERE is the domain: what a
 * reader searches for, which of a drawing's moves is the repeated one, and what
 * belongs in the export.
 *
 *   * **The row's one action is the next step in the drawing's own life** —
 *     "Start" while it is planned, "Issue revision" once it is running, and
 *     "Open" once there is nowhere forward. Waiving and deleting are real moves
 *     and both stay in the overflow menu: a register whose one-click action
 *     waives a drawing is a register that waives drawings by accident.
 *   * **Issuing is never one click.** It opens the form, because a revision code
 *     that has gone out can never go out again.
 *   * **The revision history moved into the record's drawer.** It used to open
 *     as a panel below the whole table, so the drawing being read and its own
 *     history were never on screen together.
 */

const STATUS_LABELS: Readonly<Record<DeliverableStatus, string>> = {
  planned: 'Planned',
  in_progress: 'In progress',
  issued: 'Issued',
  superseded: 'Superseded',
  cancelled: 'Waived',
};

const STATUS_SYMBOLS: Readonly<Record<DeliverableStatus, string>> = {
  planned: '○',
  in_progress: '◐',
  issued: '✓',
  superseded: '–',
  cancelled: '×',
};

const DISCIPLINE_LABELS: Readonly<Record<Discipline, string>> = {
  architectural: 'Architectural',
  structural: 'Structural',
  mep: 'MEP',
  joinery: 'Joinery',
  interior: 'Interior',
  other: 'Other',
};

const MIN_REASON = 20;

/** Still owed. Drives the count above the register and the "Show" filter. */
const OUTSTANDING: readonly DeliverableStatus[] = ['planned', 'in_progress'];

/**
 * Whether a revision may be issued against this drawing at all.
 *
 * A waived drawing is not wanted and a superseded one is not the current record
 * of anything, so neither is offered — the same two the service refuses.
 */
function mayIssue(drawing: Deliverable): boolean {
  return drawing.status !== 'cancelled' && drawing.status !== 'superseded';
}

interface Props {
  readonly projectId: string;
  /**
   * One drawing, as its own page, from `/projects/<id>/design/drawings/<id>`.
   *
   * A drawing EARNS a page: its revision history is a list, each revision
   * carries the file that was issued, and the submittals raised against it are
   * another list. That is three things a 480px drawer stacks and a page lays
   * out. See `Materials.tsx` for why the page is a mode of the register rather
   * than a screen beside it.
   */
  readonly recordId?: string | null;
  readonly can: {
    readonly write: boolean;
    readonly edit: boolean;
    readonly remove: boolean;
    /** `deliverable: X`. Gates the button; the rows are already filtered. */
    readonly exportRows: boolean;
  };
}

/**
 * What the drawer is doing. `null` means it is shut.
 *
 * `detail` is gone: the drawing has a page and an address now (§4.3). What is
 * left here is the four FORMS, because a form is not a record and does not want
 * an address of its own.
 */
type DrawerMode =
  | { readonly kind: 'edit'; readonly id: string }
  | { readonly kind: 'issue'; readonly id: string }
  | { readonly kind: 'waive'; readonly id: string }
  | { readonly kind: 'add' };

export function Drawings({ projectId, recordId = null, can }: Props): JSX.Element {
  const [items, setItems] = useState<readonly Deliverable[] | null>(null);
  /** How many exist on the server, vs how many are loaded — see `api/paging.ts`. */
  const [registerTotal, setRegisterTotal] = useState(0);
  const [wantAll, setWantAll] = useState(false);
  /**
   * Not the same as an empty register. A QS holds no `deliverable` permission
   * at all, and "No drawings yet" would tell them the job has none — which is
   * a different and wrong thing to say.
   */
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [drawer, setDrawer] = useState<DrawerMode | null>(null);
  /** Set by the form inside the drawer, so closing it can ask before discarding. */
  const [formDirty, setFormDirty] = useState(false);
  const view = useRegisterView('promised');
  /**
   * What has been sent out against each drawing.
   *
   * The relationship is on the SUBMITTAL (`deliverable_id`), so the drawings
   * register cannot see it without asking. Allowed to fail silently: a Design
   * Coordinator who may read drawings and not submittals loses this line, not
   * the screen.
   */
  const [submittals, setSubmittals] = useState<readonly Submittal[]>([]);

  const load = useCallback(async () => {
    try {
      const page = await listDeliverables(projectId, wantAll ? LOAD_ALL_LIMIT : undefined);
      setItems(page.rows);
      setRegisterTotal(page.total);
      setDenied(false);
    } catch (caught) {
      const refused = caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
      setDenied(refused);
      setError(refused ? null : 'Could not load the drawings.');
      setItems([]);
    }
    try {
      // A supporting list, not this screen's register: only the rows.
      setSubmittals((await listSubmittals(projectId)).rows);
    } catch {
      setSubmittals([]);
    }
  }, [projectId, wantAll]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(work: () => Promise<readonly Deliverable[]>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      setItems(await work());
      return true;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  /** Shut the drawer, and forget whether its form had edits in it. */
  const closeDrawer = useCallback(() => {
    setDrawer(null);
    setFormDirty(false);
    setError(null);
  }, []);

  /**
   * The rows on screen.
   *
   * Filtering lives HERE rather than in `useRegisterView`, so every dependency
   * is visible to `react-hooks/exhaustive-deps` — which this repo treats as an
   * error. It runs before the early returns below because hooks must.
   */
  const shown = useMemo(() => {
    if (!items) return [];

    const stage = view.chosen.stage;
    const discipline = view.chosen.discipline;

    const filtered = items.filter((d) => {
      // Everything somebody would type: the number they say out loud, what the
      // drawing is of, its discipline, and the revision that is current.
      const hay = [d.number, d.title, DISCIPLINE_LABELS[d.discipline], d.current_revision]
        .filter(Boolean)
        .join(' ');
      if (!matches(hay, view.query)) return false;
      if (discipline && d.discipline !== discipline) return false;
      if (stage === 'outstanding' && !OUTSTANDING.includes(d.status)) return false;
      if (stage === 'late' && !d.overdue) return false;
      if (stage === 'issued' && d.status !== 'issued') return false;
      if (stage === 'waived' && d.status !== 'cancelled') return false;
      return true;
    });

    // A null date sorts LAST, never first. A drawing nobody has dated is not the
    // most urgent thing on the job; it is the one nobody has promised.
    const byDate = (a: Deliverable, b: Deliverable): number => {
      const x = a.planned_issue_date;
      const y = b.planned_issue_date;
      if (x === y) return 0;
      if (x === null) return 1;
      if (y === null) return -1;
      return x < y ? -1 : 1;
    };

    const compare: Readonly<Record<string, (a: Deliverable, b: Deliverable) => number>> = {
      promised: byDate,
      // Worst first. A drawing that is not late has no lateness to compare, and
      // sorts below every one that has.
      late: (a, b) => (b.days_late ?? -1) - (a.days_late ?? -1),
      number: (a, b) => a.number.localeCompare(b.number),
      discipline: (a, b) =>
        DISCIPLINE_LABELS[a.discipline].localeCompare(DISCIPLINE_LABELS[b.discipline]) ||
        a.number.localeCompare(b.number),
    };

    return [...filtered].sort(compare[view.sortKey] ?? compare.promised!);
  }, [items, view.query, view.chosen.stage, view.chosen.discipline, view.sortKey]);

  /** The record the drawer is about. `null` for the add form, which has none. */
  const subject =
    drawer && drawer.kind !== 'add' ? (items?.find((d) => d.id === drawer.id) ?? null) : null;

  /**
   * The one move that goes on the row.
   *
   * The lifecycle forwards, exactly as `Materials.tsx` reads its own: start it,
   * then issue against it. `cancelled` is absent on purpose — waiving is a
   * decision that demands a written reason, and it belongs in the menu.
   */
  function primaryFor(drawing: Deliverable): RowAction {
    if (can.edit && drawing.allowed_next.includes('in_progress')) {
      return {
        label: 'Start',
        disabled: busy,
        onSelect: () =>
          void run(() => moveDeliverable(projectId, drawing.id, { status: 'in_progress' })),
      };
    }
    if (can.edit && mayIssue(drawing)) {
      return {
        label: 'Issue revision',
        disabled: busy,
        onSelect: () => setDrawer({ kind: 'issue', id: drawing.id }),
      };
    }
    return {
      label: 'Open',
      onSelect: () => navigate(recordHref(projectId, 'drawings', drawing.id)),
    };
  }

  /**
   * Everything that is not the one visible action.
   *
   * The same list serves the row and the record page. Two entries differ, and
   * both for the same reason: on the page you are already looking at the record.
   */
  function overflowFor(
    drawing: Deliverable,
    primary: RowAction | null,
    onPage = false,
  ): readonly RowAction[] {
    const actions: RowAction[] = onPage
      ? []
      : [
          {
            label: 'Open',
            onSelect: () => navigate(recordHref(projectId, 'drawings', drawing.id)),
          },
        ];
    if (can.edit && mayIssue(drawing)) {
      actions.push({
        label: 'Issue revision',
        disabled: busy,
        onSelect: () => setDrawer({ kind: 'issue', id: drawing.id }),
      });
    }
    if (can.edit && drawing.allowed_next.includes('in_progress')) {
      actions.push({
        label: 'Start',
        disabled: busy,
        onSelect: () =>
          void run(() => moveDeliverable(projectId, drawing.id, { status: 'in_progress' })),
      });
    }
    if (can.edit) {
      actions.push({ label: 'Edit', onSelect: () => setDrawer({ kind: 'edit', id: drawing.id }) });
    }
    // The register never offers a move the machine has not returned, so a button
    // here cannot be one the service then refuses.
    if (can.edit && drawing.allowed_next.includes('cancelled')) {
      actions.push({
        label: 'Waive',
        danger: true,
        disabled: busy,
        onSelect: () => setDrawer({ kind: 'waive', id: drawing.id }),
      });
    }
    if (can.remove) {
      actions.push({
        label: 'Delete',
        danger: true,
        disabled: busy,
        onSelect: () => {
          void run(() => deleteDeliverable(projectId, drawing.id)).then((ok) => {
            // Deleting the record you are standing on leaves you on an address
            // that names nothing. The register is where the deletion makes
            // sense, so that is where it puts you.
            if (ok && onPage) navigate(sectionHref(projectId, 'design'));
          });
        },
      });
    }
    // Whatever is already on the row must not also be in the menu behind it.
    return primary ? actions.filter((action) => action.label !== primary.label) : actions;
  }

  async function exportRows(): Promise<void> {
    const headers = [
      'Drawing',
      'Title',
      'Discipline',
      'Status',
      'Promised',
      'Current revision',
      'Revisions issued',
      'Days late',
      'Waived reason',
    ];
    const rows = shown.map((d) => [
      d.number,
      d.title,
      DISCIPLINE_LABELS[d.discipline],
      STATUS_LABELS[d.status],
      d.planned_issue_date ?? '',
      d.current_revision ?? '',
      String(d.revision_count),
      // Late means STILL OWED, not historically late, so an issued drawing
      // carries no lateness here either — the same rule the register shows.
      d.overdue && d.days_late !== null ? String(d.days_late) : '',
      d.cancelled_reason ?? '',
    ]);
    setError(null);
    try {
      // Recorded BEFORE the file is written, and refusable — `09` §Threats
      // wants the row count and the filters. See `register-export.ts`.
      await exportRegister({
        projectId,
        resource: 'deliverable',
        filename: `drawings-${projectId.slice(0, 8)}.csv`,
        headers,
        rows,
        filters: { query: view.query, ...view.chosen },
      });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not export.');
    }
  }

  const head = (
    <div className="section-head sub-head-row">
      <h3 className="sub-head">Drawings</h3>
      {items && can.write && (
        <button type="button" className="inline" onClick={() => setDrawer({ kind: 'add' })}>
          Add drawing
        </button>
      )}
    </div>
  );

  /*
   * A record page has the SAME four states as the register, and they stay four
   * different sentences. What changes is the furniture above them: the
   * register's head carries "Add drawing", which is the wrong offer to somebody
   * who followed a link to one particular drawing. They get the way back.
   */
  const above = recordId ? <RecordBack projectId={projectId} register="drawings" /> : head;

  if (denied) {
    return (
      <>
        {above}
        <p className="hint">Your role does not include this project&rsquo;s drawings.</p>
      </>
    );
  }

  if (items === null) {
    return (
      <>
        {above}
        <p aria-live="polite">Loading {recordId ? 'the drawing' : 'the drawings'}…</p>
      </>
    );
  }

  const outstanding = items.filter((d) => OUTSTANDING.includes(d.status));
  const late = outstanding.filter((d) => d.overdue);

  /**
   * The four forms, shared by the register and the record page.
   *
   * The same drawers on both, deliberately: issuing a revision and waiving a
   * drawing are `05` rules — a revision code that has gone out can never go out
   * again, and a waiver demands a written reason — and a page with its own copy
   * of them is a page that will still be enforcing the old rule after somebody
   * changes it.
   */
  const drawers = (
    <>
      {drawer?.kind === 'add' && (
        <Drawer title="Add a drawing" dirty={formDirty} onClose={closeDrawer}>
          <AddForm
            busy={busy}
            onDirtyChange={setFormDirty}
            onCancel={closeDrawer}
            onSubmit={async (input) => {
              if (await run(() => createDeliverable(projectId, input))) closeDrawer();
            }}
          />
        </Drawer>
      )}

      {drawer?.kind === 'edit' && subject && (
        <Drawer
          title={`Edit ${subject.number}`}
          subtitle={subject.title}
          dirty={formDirty}
          onClose={closeDrawer}
        >
          <EditForm
            drawing={subject}
            busy={busy}
            onDirtyChange={setFormDirty}
            onCancel={closeDrawer}
            onSubmit={async (input) => {
              if (await run(() => updateDeliverable(projectId, subject.id, input))) closeDrawer();
            }}
          />
        </Drawer>
      )}

      {drawer?.kind === 'issue' && subject && (
        <Drawer
          title={`Issue a revision of ${subject.number}`}
          subtitle={subject.title}
          dirty={formDirty}
          onClose={closeDrawer}
        >
          <IssueForm
            drawing={subject}
            busy={busy}
            onDirtyChange={setFormDirty}
            onCancel={closeDrawer}
            onSubmit={async (input) => {
              if (await run(() => issueRevision(projectId, subject.id, input))) closeDrawer();
            }}
          />
        </Drawer>
      )}

      {drawer?.kind === 'waive' && subject && (
        <Drawer
          title={`Waive ${subject.number}`}
          subtitle={subject.title}
          dirty={formDirty}
          onClose={closeDrawer}
        >
          <WaiveForm
            busy={busy}
            onDirtyChange={setFormDirty}
            onCancel={closeDrawer}
            onSubmit={async (reason) => {
              if (
                await run(() =>
                  moveDeliverable(projectId, subject.id, { status: 'cancelled', reason }),
                )
              ) {
                closeDrawer();
              }
            }}
          />
        </Drawer>
      )}
    </>
  );

  const alert = error && (
    <div className="alert" role="alert">
      <span>{error}</span>
    </div>
  );

  // ------------------------------------------------------- one drawing, in full
  if (recordId) {
    const record = items.find((d) => d.id === recordId) ?? null;
    if (!record) {
      /*
       * A FAILED LOAD IS NOT A MISSING RECORD. The load empties the list when
       * the request breaks, so a perfectly valid id finds nothing — and "that
       * drawing is not here" would be a confident, wrong answer.
       */
      return error ? (
        <>
          {above}
          {alert}
        </>
      ) : (
        <RecordMissing projectId={projectId} register="drawings" />
      );
    }

    /*
     * `primaryFor` falls back to "Open" when there is nowhere forward to go. On
     * the record page that would be a button that navigates to the page you are
     * already on, so it is dropped and the page carries only its More menu —
     * which is the honest answer for a waived or superseded drawing.
     */
    const forward = primaryFor(record);
    const primary = forward.label === 'Open' ? null : forward;
    /*
     * NO BAR WHEN THERE IS NOTHING IN IT. A QS holds no `deliverable` grant at
     * all and a Client holds read-only ones, so both would otherwise get an
     * empty bordered box where the moves live — which says "there is something
     * here you cannot have" rather than saying nothing.
     */
    const more = overflowFor(record, primary, true);
    const actions =
      primary || more.length > 0 ? (
        <RowActions rowLabel={record.number} {...(primary ? { primary } : {})} more={more} />
      ) : null;
    const sentOut = raisedAgainst(submittals, record.id);

    return (
      <>
        {alert}
        <RecordPage
          projectId={projectId}
          register="drawings"
          reference={record.number}
          title={record.title}
          status={STATUS_LABELS[record.status]}
          meta={
            <>
              {DISCIPLINE_LABELS[record.discipline]}
              {/*
                Lateness is on the head for the same reason it stays on the row
                at both densities: it is the one fact that changes what somebody
                does next. Said in words, never by colour alone.
              */}
              {record.overdue && (
                <span className="task-overdue">
                  {' · '}
                  {record.days_late} {record.days_late === 1 ? 'day' : 'days'} past its date
                </span>
              )}
            </>
          }
          {...(actions ? { actions } : {})}
          properties={<DrawingProperties drawing={record} />}
        >
          {/*
            THE MAIN COLUMN IS WHAT THE DRAWING IS DOING. The revision history is
            the record of what was actually issued, and each revision carries the
            file — without which a revision is a row claiming a drawing went out
            with no way to see what did. What was sent for approval off the back
            of it is the other half of the same question.
          */}
          <div className="register record-section">
            <div className="section-head sub-head-row">
              <h4 className="sub-head">Revisions</h4>
            </div>
            {record.revisions.length > 0 ? (
              <RevisionHistory drawing={record} projectId={projectId} canIssue={can.edit} />
            ) : (
              <p className="hint">
                Nothing has been issued yet. Issuing a revision is what makes this drawing count
                as issued on the Design stage.
              </p>
            )}
          </div>

          <div className="register record-section">
            <div className="section-head sub-head-row">
              <h4 className="sub-head">Sent for approval</h4>
            </div>
            {sentOut.length > 0 ? (
              <p className="hint">
                <SentOut projectId={projectId} submittals={sentOut} bare />
              </p>
            ) : (
              /*
               * An empty state has to name an action ITS reader can take, and
               * whoever raises a submittal is a Design Coordinator rather than
               * this drawing's owner — so it says where, not "add one".
               */
              <p className="hint">
                Nothing has been sent for approval off this drawing yet. Submittals are raised on
                the submittals register, below the drawings.
              </p>
            )}
          </div>
        </RecordPage>
        {drawers}
      </>
    );
  }

  return (
    <>
      {head}

      {alert}

      {items.length > 0 && (
        <p className="hint" role="status">
          <strong>
            {outstanding.length} of {items.length} still to issue
          </strong>
          {late.length > 0 && (
            <>
              {' · '}
              <span className="task-overdue">
                {late.length} past {late.length === 1 ? 'its' : 'their'} promised date
              </span>
            </>
          )}
          . The Design stage cannot close until every drawing is issued or waived.
        </p>
      )}

      {items.length === 0 ? (
        <div className="empty">
          <p>
            <strong>No drawings yet.</strong>
          </p>
          <p>
            {can.write
              ? 'Add what the design owes and the date it is promised for. A drawing counts as done when a revision has been issued against it — not when somebody changes its status.'
              : /*
                 * An empty state must name an action ITS READER can take. A Site
                 * Engineer or a Client holds `deliverable: V` and no create, so
                 * the sentence for them says who does it, rather than pointing
                 * at a button that is correctly absent.
                 */
                'Nothing has been added yet. The Design Coordinator or the Project Manager lists what the design owes and when it is promised, and a drawing counts as done once a revision has been issued against it.'}
          </p>
        </div>
      ) : (
        <>
          <RegisterToolbar
            view={view}
            searchLabel="Search drawings"
            searchPlaceholder="Number, title, discipline"
            shown={shown.length}
            total={items.length}
            registerTotal={registerTotal}
            onLoadAll={() => setWantAll(true)}
            loadingAll={wantAll && items.length < registerTotal}
            noun={['drawing', 'drawings']}
            filters={[
              {
                key: 'stage',
                label: 'Show',
                options: [
                  { value: 'outstanding', label: 'Still to issue' },
                  { value: 'late', label: 'Past its promised date' },
                  { value: 'issued', label: 'Issued' },
                  { value: 'waived', label: 'Waived' },
                ],
              },
              {
                key: 'discipline',
                label: 'Discipline',
                options: DISCIPLINES.map((d) => ({ value: d, label: DISCIPLINE_LABELS[d] })),
              },
            ]}
            sorts={[
              { key: 'promised', label: 'Promised date' },
              { key: 'late', label: 'Most overdue' },
              { key: 'number', label: 'Drawing number' },
              { key: 'discipline', label: 'Discipline' },
            ]}
          >
            {can.exportRows && (
              <button type="button" className="secondary inline" onClick={() => void exportRows()}>
                Export {shown.length === items.length ? 'all' : 'these'} to CSV
              </button>
            )}
          </RegisterToolbar>

          {shown.length === 0 ? (
            /*
             * A DIFFERENT empty state from "no drawings yet". This reader has a
             * register full of drawings and a filter that excludes all of them;
             * telling them to add one would be advice about the wrong problem.
             */
            <div className="empty">
              <p>
                <strong>No drawing matches that.</strong>
              </p>
              <p>
                <button type="button" className="link-button" onClick={view.clear}>
                  Clear the search and filters
                </button>{' '}
                to see all {items.length}.
              </p>
            </div>
          ) : (
            <div className={`rk-scroll${view.density === 'compact' ? ' rk-compact' : ''}`}>
              <table className="grid rk-cards">
                <caption className="visually-hidden">
                  The drawings register, with the promised date, the current revision and what is
                  late. {shown.length} of {items.length} shown.
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Drawing</th>
                    <th scope="col">Discipline</th>
                    <th scope="col">Promised</th>
                    <th scope="col">Current revision</th>
                    <th scope="col">
                      <span className="visually-hidden">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((d) => {
                    const primary = primaryFor(d);
                    return (
                      <DrawingRow key={d.id} drawing={d}>
                        <td>
                          <span className="ms-symbol" aria-hidden="true">
                            {STATUS_SYMBOLS[d.status]}
                          </span>{' '}
                          {/*
                            The number is the way IN to the record. A register
                            whose identifier is not clickable makes people hunt
                            for a row action to see the thing they just read.

                            A REAL LINK, because the record has an address now —
                            so a middle-click, a new tab and a copied link all
                            mean what they look like they mean.
                          */}
                          <AppLink className="rk-open" to={recordHref(projectId, 'drawings', d.id)}>
                            <strong>{d.number}</strong> {d.title}
                            <span className="visually-hidden"> — open this drawing</span>
                          </AppLink>
                          <span className="tree-code"> {STATUS_LABELS[d.status]}</span>
                          {/*
                            Lateness stays at BOTH densities. It is the one fact
                            on the row that changes what somebody does next.
                          */}
                          {d.overdue && (
                            <span className="task-overdue">
                              {' '}
                              · {d.days_late} {d.days_late === 1 ? 'day' : 'days'} past its date
                            </span>
                          )}
                          {/*
                            SECONDARY. Hidden at compact density — and both of
                            these are on the record's drawer too, so nothing is
                            visible at only one density.
                          */}
                          <span className="rk-secondary">
                            {d.cancelled_reason && (
                              <span className="tree-code"> Waived: {d.cancelled_reason}</span>
                            )}
                            <SentOut
                              projectId={projectId}
                              submittals={raisedAgainst(submittals, d.id)}
                            />
                          </span>
                        </td>
                        <td data-label="Discipline">{DISCIPLINE_LABELS[d.discipline]}</td>
                        <td data-label="Promised">{formatDate(d.planned_issue_date)}</td>
                        <td data-label="Current revision">
                          {d.current_revision ? (
                            <>
                              Rev {d.current_revision}{' '}
                              {d.revision_count > 1 && (
                                <AppLink
                                  className="link-button"
                                  to={recordHref(projectId, 'drawings', d.id)}
                                >
                                  {d.revision_count} revisions
                                  <span className="visually-hidden"> of {d.number}</span>
                                </AppLink>
                              )}
                            </>
                          ) : (
                            <span className="tree-code">Nothing issued</span>
                          )}
                        </td>
                        <td>
                          <RowActions
                            rowLabel={d.number}
                            primary={primary}
                            more={overflowFor(d, primary)}
                          />
                        </td>
                      </DrawingRow>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {drawers}
    </>
  );
}

// ---------------------------------------------------------------------------

/**
 * What the drawing IS — the record page's rail.
 *
 * Everything compact density hides is here. This was the drawer's whole body
 * until the drawing earned a page; what changed is only where the parts sit —
 * the FACTS here, and the two lists (the revision history, and what was sent
 * for approval off the back of it) in the main column, laid out rather than
 * stacked in a 480px panel. The revision history in particular used to open as
 * a panel under the whole table, so the drawing being read and its own history
 * were never on screen together; on a page they always are.
 */
function DrawingProperties({ drawing }: { readonly drawing: Deliverable }): JSX.Element {
  return (
    <>
      <dl className="detail-grid">
        <dt>Status</dt>
        <dd>
          {STATUS_LABELS[drawing.status]}
          {drawing.overdue && (
            <span className="task-overdue">
              {' '}
              · {drawing.days_late} {drawing.days_late === 1 ? 'day' : 'days'} past its date
            </span>
          )}
        </dd>
        <dt>Discipline</dt>
        <dd>{DISCIPLINE_LABELS[drawing.discipline]}</dd>
        <dt>Promised</dt>
        <dd>{formatDate(drawing.planned_issue_date)}</dd>
        <dt>First issued</dt>
        <dd>{formatDate(drawing.actual_issue_date)}</dd>
        <dt>Current revision</dt>
        <dd>{drawing.current_revision ? `Rev ${drawing.current_revision}` : 'Nothing issued'}</dd>
        {drawing.owner_name && (
          <>
            <dt>Owner</dt>
            <dd>{drawing.owner_name}</dd>
          </>
        )}
        {drawing.location_path && (
          <>
            <dt>Where</dt>
            <dd>{drawing.location_path}</dd>
          </>
        )}
        {drawing.cancelled_reason && (
          <>
            <dt>Waived because</dt>
            <dd>{drawing.cancelled_reason}</dd>
          </>
        )}
      </dl>
    </>
  );
}

// ---------------------------------------------------------------------------

/** The submittals raised against one drawing. */
function raisedAgainst(
  submittals: readonly Submittal[],
  deliverableId: string,
): readonly Submittal[] {
  return submittals.filter((s) => s.deliverable_id === deliverableId);
}

/**
 * What has been sent out against this drawing, and how it stands.
 *
 * A drawing is only late in the way that matters if what it feeds is waiting,
 * so the row says which submittals came off it and marks the overdue ones —
 * the same fact the submittals register leads with, said where the drawing is.
 */
function SentOut({
  projectId,
  submittals,
  /**
   * Drop the leading words, for a caller whose own heading already says them.
   *
   * On the register this line has to introduce itself — it appears mid-row among
   * four other pieces of secondary detail. Under a section headed "Sent for
   * approval" on the record page it would read "Sent for approval — Sent for
   * approval: SUB-0001", and two headings for one list read as a mistake.
   */
  bare = false,
}: {
  readonly projectId: string;
  readonly submittals: readonly Submittal[];
  readonly bare?: boolean;
}): JSX.Element | null {
  if (submittals.length === 0) return null;
  const late = submittals.filter((s) => s.sla === 'overdue' || s.sla === 'red').length;

  return (
    <span className="tree-code">
      {' '}
      {!bare && <>Sent for approval: </>}
      {submittals.map((s, index) => (
        <span key={s.id}>
          {index > 0 && ', '}
          <CrossLink
            projectId={projectId}
            section="design"
            register="submittals"
            focus={s.id}
            says="a submittal raised against this drawing"
          >
            {s.number}
          </CrossLink>
        </span>
      ))}
      {late > 0 && (
        <span className="task-overdue">
          {' '}
          · {late} past {late === 1 ? 'its' : 'their'} response date
        </span>
      )}
    </span>
  );
}

/** One row, which knows whether a cross-link came looking for it. */
function DrawingRow({
  drawing,
  children,
}: {
  readonly drawing: Deliverable;
  readonly children: React.ReactNode;
}): JSX.Element {
  const { focused, ref } = useFocusRow<HTMLTableRowElement>(drawing.id);
  return (
    <tr
      ref={ref}
      className={focusClass(focused, `dwg-${drawing.status}${drawing.overdue ? ' dwg-overdue' : ''}`)}
      aria-current={focused ? 'true' : undefined}
    >
      {children}
    </tr>
  );
}

// ---------------------------------------------------------------------------

/**
 * Every revision that was ever issued, including the ones that are not current.
 *
 * `05` §9: the person on site may be holding Rev B while Rev D is current, and
 * the system has to be able to say which was current on which date. That is
 * only true if the superseded ones are still on the screen.
 */
function RevisionHistory({
  drawing,
  projectId,
  canIssue,
}: {
  readonly drawing: Deliverable;
  readonly projectId: string;
  readonly canIssue: boolean;
}): JSX.Element {
  return (
    <div className="panel">
      {/*
        NO TITLE OF ITS OWN. The record page's section is already headed
        "Revisions" and the page's own title is already the drawing's number, so
        "DWG-102 — every revision issued" underneath it is the third time in
        four lines. Same rule that took `panel-title` off every form when the
        drawer landed.
      */}
      <ul className="dwg-revisions">
        {drawing.revisions.map((r) => (
          <li key={r.id}>
            <strong>Rev {r.revision_code}</strong>{' '}
            <span className="tree-code">
              {r.is_current ? 'Current' : `Superseded ${formatDate(r.superseded_at)}`}
            </span>
            <span className="diary-late">
              {' '}
              Issued {formatDate(r.issued_at)} by {r.issued_by_name ?? 'someone'}
            </span>
            {r.notes && <span className="diary-summary"> {r.notes}</span>}
            {/*
              The drawing FILE itself. Without it a revision is a row claiming a
              drawing was issued with no way to see WHAT was issued — which is
              precisely the argument the revision history exists to settle.

              Shared by default: a revision is issued TO somebody, and a
              consultant who cannot open it has not received it.
            */}
            <Attachments
              projectId={projectId}
              entityType="drawing_revision"
              entityId={r.id}
              can={{ write: canIssue }}
              noun="the drawing"
              category="drawing"
              shareByDefault
            />
          </li>
        ))}
      </ul>
      <p className="hint">
        Superseded revisions are kept and never deleted. Somebody on site may still be holding a
        printout of one.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------

interface AddProps {
  readonly busy: boolean;
  readonly onCancel: () => void;
  /** Lets the drawer ask before throwing away what has been typed. */
  readonly onDirtyChange?: (dirty: boolean) => void;
  readonly onSubmit: (input: {
    number: string;
    title: string;
    discipline: Discipline;
    planned_issue_date: string | null;
    revision_code: string | null;
  }) => void | Promise<void>;
}

/**
 * The revision a new drawing is born at.
 *
 * `0` because that is what the user said the act means: *"once i add the
 * drawings that means this is revision 0"*. Recording a drawing you already
 * have is the common case, and it used to take three steps — add, Start, Issue
 * revision — for something that had already happened.
 *
 * CLEARING THE BOX is how you ask for the other kind of row: a drawing the job
 * still OWES, with a promised date and nothing issued against it. That row is
 * what the Design gate and the late-drawing warning are built on, so it has to
 * stay reachable. The trade the user was shown and accepted is that it becomes
 * the deliberate case rather than the default.
 */
const FIRST_REVISION = '0';

function AddForm({ busy, onCancel, onDirtyChange, onSubmit }: AddProps): JSX.Element {
  const [number, setNumber] = useState('');
  const [title, setTitle] = useState('');
  const [discipline, setDiscipline] = useState<Discipline>('architectural');
  const [date, setDate] = useState('');
  const [revision, setRevision] = useState(FIRST_REVISION);

  /*
   * Compared against the empty form rather than tracked with a touched flag:
   * somebody who types a character and deletes it has changed nothing and
   * should not be asked to confirm. The discipline and the revision both start
   * at a real value, so they are compared against that rather than nothing —
   * and CLEARING the revision counts as a change, because it is one.
   */
  useEffect(() => {
    onDirtyChange?.(
      number !== '' ||
        title !== '' ||
        date !== '' ||
        discipline !== 'architectural' ||
        revision !== FIRST_REVISION,
    );
  }, [number, title, date, discipline, revision, onDirtyChange]);

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!number.trim() || !title.trim()) return;
    void onSubmit({
      number: number.trim(),
      title: title.trim(),
      discipline,
      planned_issue_date: date || null,
      revision_code: revision.trim() || null,
    });
  }

  return (
    <form className="panel" onSubmit={submit}>
      <div className="field-row">
        <div className="field">
          <label htmlFor="dwg-number">Drawing number</label>
          <input
            id="dwg-number"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            maxLength={40}
            disabled={busy}
            placeholder="AR-101"
          />
          <p className="hint">What people say out loud. It cannot be changed afterwards.</p>
        </div>
        <div className="field">
          <label htmlFor="dwg-title">Title</label>
          <input
            id="dwg-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            disabled={busy}
            placeholder="Reception ceiling plan"
          />
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="dwg-discipline">Discipline</label>
          <select
            id="dwg-discipline"
            value={discipline}
            disabled={busy}
            onChange={(e) => setDiscipline(e.target.value as Discipline)}
          >
            {DISCIPLINES.map((d) => (
              <option key={d} value={d}>
                {DISCIPLINE_LABELS[d]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="dwg-date">Promised by</label>
          <input
            id="dwg-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            disabled={busy}
          />
          <p className="hint">Past this date with nothing issued, it counts as late.</p>
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="dwg-revision">Revision</label>
          <input
            id="dwg-revision"
            value={revision}
            onChange={(e) => setRevision(e.target.value)}
            maxLength={10}
            disabled={busy}
            placeholder="0"
          />
          {/*
            The hint carries the whole of the decision, because the box looks
            optional and the empty case is the one that changes what the row
            MEANS — not a detail of it. Somebody who clears this is asking for a
            drawing the job owes; somebody who leaves it is recording one they
            have. Neither is obvious from a field called "Revision".
          */}
          <p className="hint">
            The drawing goes on the register as issued at this revision. <strong>Clear it</strong>{' '}
            if the drawing does not exist yet and you are only recording that it is owed.
          </p>
        </div>
      </div>

      <div className="button-row">
        <button
          type="submit"
          className="inline"
          disabled={busy || !number.trim() || !title.trim()}
        >
          {busy ? 'Adding…' : 'Add drawing'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------

interface IssueProps {
  readonly drawing: Deliverable;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onDirtyChange?: (dirty: boolean) => void;
  readonly onSubmit: (input: { revision_code: string; notes: string | null }) => void | Promise<void>;
}

function IssueForm({
  drawing,
  busy,
  onCancel,
  onDirtyChange,
  onSubmit,
}: IssueProps): JSX.Element {
  const [code, setCode] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    onDirtyChange?.(code !== '' || notes !== '');
  }, [code, notes, onDirtyChange]);

  return (
    // The drawer's own heading already says which drawing this is, so the panel
    // does not say it a second time.
    <div className="panel">
      <p className="hint">
        {drawing.current_revision
          ? `Rev ${drawing.current_revision} is current. Issuing a new one supersedes it — the old one is kept and stays readable.`
          : 'Nothing has been issued yet. This is what makes the drawing count as issued on the Design stage.'}
      </p>

      <div className="field-row">
        <div className="field">
          <label htmlFor="rev-code">Revision</label>
          <input
            id="rev-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={20}
            disabled={busy}
            placeholder="A"
          />
          <p className="hint">Raise the next one. A code that has gone out cannot go out again.</p>
        </div>
      </div>

      <div className="field">
        <label htmlFor="rev-notes">What changed?</label>
        <textarea
          id="rev-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          maxLength={2000}
          disabled={busy}
          placeholder="Ceiling grid revised to suit the revised MEP layout."
        />
      </div>

      <div className="button-row">
        <button
          type="button"
          className="inline"
          disabled={busy || !code.trim()}
          onClick={() => void onSubmit({ revision_code: code.trim(), notes: notes.trim() || null })}
        >
          {busy ? 'Issuing…' : 'Issue it'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

interface EditProps {
  readonly drawing: Deliverable;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onDirtyChange?: (dirty: boolean) => void;
  readonly onSubmit: (input: {
    title: string;
    discipline: Discipline;
    planned_issue_date: string | null;
    revision_code?: string;
  }) => void | Promise<void>;
}

function EditForm({ drawing, busy, onCancel, onDirtyChange, onSubmit }: EditProps): JSX.Element {
  const [title, setTitle] = useState(drawing.title);
  const [discipline, setDiscipline] = useState<Discipline>(drawing.discipline);
  const [date, setDate] = useState(drawing.planned_issue_date ?? '');
  /**
   * The current revision's code, correctable in place.
   *
   * Asked for during testing — *"in the edit tab i should be able to adjust the
   * revision number as well"* — after a `0` was typed as `00`.
   *
   * Absent when nothing has been issued: there is no revision to rename, and a
   * box that silently did nothing would be worse than no box. Issuing the FIRST
   * revision is the "Issue revision" action, and issuing the NEXT one still is
   * too — this only ever renames.
   */
  const [revision, setRevision] = useState(drawing.current_revision ?? '');
  const hasRevision = drawing.current_revision !== null;

  // Compared against the RECORD, so retyping a value back to what it already
  // was leaves the form clean.
  useEffect(() => {
    onDirtyChange?.(
      title !== drawing.title ||
        discipline !== drawing.discipline ||
        date !== (drawing.planned_issue_date ?? '') ||
        revision !== (drawing.current_revision ?? ''),
    );
  }, [title, discipline, date, revision, drawing, onDirtyChange]);

  return (
    <div className="panel">
      <div className="field-row">
        <div className="field">
          <label htmlFor="dwg-edit-title">Title</label>
          <input
            id="dwg-edit-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            disabled={busy}
          />
        </div>
        <div className="field">
          <label htmlFor="dwg-edit-date">Promised by</label>
          <input
            id="dwg-edit-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            disabled={busy}
          />
        </div>
      </div>

      <div className="field">
        <label htmlFor="dwg-edit-discipline">Discipline</label>
        <select
          id="dwg-edit-discipline"
          value={discipline}
          disabled={busy}
          onChange={(e) => setDiscipline(e.target.value as Discipline)}
        >
          {DISCIPLINES.map((d) => (
            <option key={d} value={d}>
              {DISCIPLINE_LABELS[d]}
            </option>
          ))}
        </select>
      </div>

      {/*
        Only when there IS one. A drawing with nothing issued has no revision to
        rename, and an empty box beside "Current revision" would invite somebody
        to type one and expect it to be issued — which is the other button's job.
      */}
      {hasRevision && (
        <div className="field">
          <label htmlFor="dwg-edit-revision">Current revision</label>
          <input
            id="dwg-edit-revision"
            value={revision}
            onChange={(e) => setRevision(e.target.value)}
            maxLength={10}
            disabled={busy}
          />
          <p className="hint">
            Corrects the code on the revision already issued — a <code>00</code> typed where{' '}
            <code>0</code> was meant. It does <strong>not</strong> issue a new one, and it is
            refused once this revision has gone out on a submittal: the code is part of what was
            sent.
          </p>
        </div>
      )}

      <p className="hint">
        The number cannot be changed here, or anywhere. It is quoted in every email and on every
        printout of the drawing.
      </p>

      <div className="button-row">
        <button
          type="button"
          className="inline"
          disabled={busy || !title.trim() || (hasRevision && !revision.trim())}
          onClick={() =>
            void onSubmit({
              title: title.trim(),
              discipline,
              planned_issue_date: date || null,
              // Sent only when it actually changed: the server refuses a rename
              // on a revision that has gone out, and an unchanged value would
              // turn every other edit on a sent drawing into that refusal.
              ...(hasRevision && revision.trim() !== (drawing.current_revision ?? '')
                ? { revision_code: revision.trim() }
                : {}),
            })
          }
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/** No `drawing`: the drawer's heading already says which one is being waived. */
interface WaiveProps {
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onDirtyChange?: (dirty: boolean) => void;
  readonly onSubmit: (reason: string) => void | Promise<void>;
}

function WaiveForm({ busy, onCancel, onDirtyChange, onSubmit }: WaiveProps): JSX.Element {
  const [reason, setReason] = useState('');

  useEffect(() => {
    onDirtyChange?.(reason !== '');
  }, [reason, onDirtyChange]);

  return (
    <div className="panel">
      <p className="hint">
        A waived drawing stops counting against the Design stage. That is the whole effect, which
        is why it needs a reason on the record — an instruction, a variation, a change of scope.
      </p>

      <div className="field">
        <label htmlFor="dwg-waive-reason">Why is it no longer needed?</label>
        <textarea
          id="dwg-waive-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          maxLength={2000}
          disabled={busy}
          placeholder="Client removed the feature wall from scope, variation VO-004 signed."
        />
        <p className="hint">
          {reason.trim().length}/{MIN_REASON} characters minimum.
        </p>
      </div>

      <div className="button-row">
        <button
          type="button"
          className="inline"
          disabled={busy || reason.trim().length < MIN_REASON}
          onClick={() => void onSubmit(reason.trim())}
        >
          {busy ? 'Saving…' : 'Waive it'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}
